import { promises as fs } from 'fs'
import { resolve } from 'path'
import type { AppMaintenanceStatus, MaintenanceConfig } from './types'
import { DEFAULT_MAINTENANCE_MESSAGE } from './defaults'
import { fetchFromAppConfiguration } from '../AppConfig/useAppConfig'

const CACHE_TTL_MS = 30_000
let cachedConfig: MaintenanceConfig | null = null
let cacheTimestamp = 0

const parseAppStatus = (value: string): AppMaintenanceStatus => {
    try {
        const parsed = JSON.parse(value) as Partial<AppMaintenanceStatus>
        return {
            enabled: parsed.enabled === true,
            message:
                typeof parsed.message === 'string' && parsed.message.length > 0
                    ? parsed.message
                    : DEFAULT_MAINTENANCE_MESSAGE,
            expectedResolutionTime:
                typeof parsed.expectedResolutionTime === 'string' ? parsed.expectedResolutionTime : undefined,
        }
    } catch {
        return { enabled: false, message: DEFAULT_MAINTENANCE_MESSAGE }
    }
}

const fetchMaintenanceConfig = async (): Promise<MaintenanceConfig> => {
    const apps: Record<string, AppMaintenanceStatus> = {}
    const appNames = ['admin', 'collection', 'account', 'reporting']

    const settingsPromises = appNames.map(async (appName) => {
        try {
            const setting = await fetchFromAppConfiguration(`maintenance/${appName}`)
            apps[appName] = parseAppStatus(setting.value || '{}')
        } catch {
            apps[appName] = { enabled: false, message: DEFAULT_MAINTENANCE_MESSAGE }
        }
    })

    let bypassIPs: string[] = []
    const bypassPromise = (async () => {
        try {
            const setting = await fetchFromAppConfiguration('maintenance/bypassIPs')
            bypassIPs = JSON.parse(setting.value || '[]') as string[]
        } catch {
            bypassIPs = []
        }
    })()

    await Promise.all([...settingsPromises, bypassPromise])

    return { apps, bypassIPs }
}

const fetchFromLocalFile = async (): Promise<MaintenanceConfig> => {
    const filePath = resolve(process.cwd(), 'maintenance-config.json')
    try {
        const content = await fs.readFile(filePath, 'utf-8')
        const parsed = JSON.parse(content) as MaintenanceConfig
        return {
            apps: parsed.apps || {},
            bypassIPs: Array.isArray(parsed.bypassIPs) ? parsed.bypassIPs : [],
        }
    } catch {
        return { apps: {}, bypassIPs: [] }
    }
}

/**
 * Fetch maintenance configuration with a 30-second cache.
 * Uses Azure App Configuration when APPCONFIG_ENDPOINT is set,
 * falls back to a local maintenance-config.json file for development.
 */
export const getCachedMaintenanceConfig = async (): Promise<MaintenanceConfig> => {
    const now = Date.now()
    if (cachedConfig && now - cacheTimestamp < CACHE_TTL_MS) {
        return cachedConfig
    }

    const config = process.env.APPCONFIG_ENDPOINT ? await fetchMaintenanceConfig() : await fetchFromLocalFile()

    cachedConfig = config
    cacheTimestamp = now
    return config
}

/** Reset the cache (primarily for testing). */
export const resetMaintenanceConfigCache = (): void => {
    cachedConfig = null
    cacheTimestamp = 0
}
